import type { FC, ReactNode, RefObject } from "react"
import { useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useClickAway } from "react-use"

import { Option } from "$/models/option"

import { Button } from "@/3514/components"
import { buildCssClassName } from "@/3514/utils"
import { type TGetValueFromTranslatedObjectFunction, useGetValueFromTranslatedObject } from "@/hooks"
import Spinner from "@/shared/spinner/Spinner"
import { LockSvg } from "@/svgs"
import Chevron from "@/svgs/Chevron"

import { emptyCallback } from "../types/functions"

import "./styles.scss"

interface IProps {
    optionsList?: Option[] | any[]
    loading?: boolean
    defaultOptionIndex?: number
    variant?: "default" | "secondary"
    backgroundWhite?: boolean
    className?: string
    disabled?: boolean
    error?: boolean
    showDisabledIcon?: boolean
    indexIncrease?: number
    children?: (props: {
        option: any
        selectedOption: number
        index: number
        onSelect: (index: number) => void
    }) => ReactNode
    onSelect: (option: any) => void
    withDisabledKeyboardNavigation?: boolean
    ariaLabelKey?: string
    isScrollable?: boolean
    scrollOptionStartCount?: number
}

const Select: FC<IProps> = ({
    optionsList,
    onSelect,
    loading = false,
    disabled = false,
    backgroundWhite = false,
    indexIncrease = 0,
    defaultOptionIndex,
    showDisabledIcon = true,
    variant = "",
    error = false,
    className = "",
    withDisabledKeyboardNavigation = false,
    children: childrenOptions,
    isScrollable = false,
    scrollOptionStartCount = 5,
    ariaLabelKey = "Select preferred language"
}) => {
    const { t } = useTranslation()
    const [isOptionsOpen, setIsOptionsOpen] = useState(false)
    const [selectedOption, setSelectedOption] = useState(defaultOptionIndex ?? 0)
    const buttonRef = useRef<HTMLButtonElement>(null)
    const contentRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const getValueFromTranslatedObject: TGetValueFromTranslatedObjectFunction = useGetValueFromTranslatedObject()

    const toggleOptions = () => {
        if (disabled) {
            return
        }
        setIsOptionsOpen(!isOptionsOpen)
    }

    const setSelectedThenCloseDropdown = index => {
        if (optionsList[index]?.disabled || disabled) {
            return
        }

        setSelectedOption(index)
        onSelect(optionsList[index])
        setIsOptionsOpen(false)
    }

    const handleKeyDown = index => e => {
        switch (e.key) {
            case " ":
            case "SpaceBar":
            case "Enter":
                e.preventDefault()
                setSelectedThenCloseDropdown(index)
                break
            default:
                break
        }
    }

    const handleListKeyDown = e => {
        switch (e.key) {
            case "Escape":
                e.preventDefault()
                setIsOptionsOpen(false)
                break
            case "ArrowUp":
                e.preventDefault()
                setSelectedOption(selectedOption - 1 >= 0 ? selectedOption - 1 : optionsList.length - 1)
                break
            case "ArrowDown":
                e.preventDefault()
                setSelectedOption(selectedOption == optionsList.length - 1 ? 0 : selectedOption + 1)
                break
            default:
                break
        }
    }

    const handleClickOutSide = e => {
        if (buttonRef?.current.contains(e.target)) {
            return
        }

        setIsOptionsOpen(false)
    }

    const selectVariants = {
        secondary: "secondary"
    }

    useClickAway(contentRef, handleClickOutSide)

    return (
        <div
            className={buildCssClassName("select-default", selectVariants[variant], {
                [className]: className,
                "background-white": backgroundWhite,
                disabled,
                error
            })}
        >
            <Button
                isContainer
                type="button"
                aria-haspopup="listbox"
                aria-expanded={isOptionsOpen}
                className={isOptionsOpen ? "expanded" : ""}
                onClick={toggleOptions}
                {...{ ref: buttonRef }}
                onKeyDown={withDisabledKeyboardNavigation ? emptyCallback : handleListKeyDown}
            >
                {loading && <Spinner width={20} height={20} />}
                <span>
                    {optionsList?.[selectedOption]?.label ?? (
                        <>
                            <span className="font-extrabold">{selectedOption + indexIncrease}: </span>
                            <span>
                                {optionsList?.[selectedOption]?.name
                                    ? getValueFromTranslatedObject(optionsList?.[selectedOption]?.name)
                                    : "Unassigned"}
                            </span>
                        </>
                    )}
                </span>
                {disabled && showDisabledIcon ? (
                    <span aria-hidden="true">
                        <LockSvg />
                    </span>
                ) : (
                    <span className="chevron-btn" aria-hidden="true">
                        <Chevron />
                    </span>
                )}
            </Button>

            <div
                className={buildCssClassName("select-default-container", isScrollable && "!overflow-y-scroll")}
                style={
                    isOptionsOpen
                        ? {
                              height: `${contentRef.current?.scrollHeight}px`,
                              ...(isScrollable && { ...{ maxHeight: `${40 * scrollOptionStartCount}px` } })
                          }
                        : { height: "0px" }
                }
                ref={contentRef}
            >
                <ul
                    className={buildCssClassName("select-default-options")}
                    role="listbox"
                    aria-label={t(ariaLabelKey)}
                    tabIndex={-1}
                    onKeyDown={withDisabledKeyboardNavigation ? emptyCallback : handleListKeyDown}
                >
                    {optionsList?.map((option, index) =>
                        childrenOptions ? (
                            childrenOptions({ option, index, selectedOption, onSelect: setSelectedThenCloseDropdown })
                        ) : (
                            <li
                                key={option.id ?? option.value}
                                role="option"
                                aria-selected={selectedOption == index}
                                className={option?.disabled ? "font-italic bg-gray-lighter" : ""}
                                data-disabled={option?.disabled}
                                tabIndex={isOptionsOpen ? 0 : -1}
                                onKeyDown={withDisabledKeyboardNavigation ? emptyCallback : handleKeyDown(index)}
                                onClick={() => {
                                    !option.disabled && setSelectedThenCloseDropdown(index)
                                }}
                            >
                                {option?.label}
                            </li>
                        )
                    )}
                </ul>
            </div>
        </div>
    )
}

export default Select
